import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiEye, FiEyeOff, FiCalendar } from 'react-icons/fi';
import { getBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost } from '../services/blog';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import BlogPostForm from '../components/Blog/BlogPostForm';
import toast from 'react-hot-toast';

const categoryColors = {
  travel_tips: 'bg-green-100 text-green-800',
  destinations: 'bg-blue-100 text-blue-800',
  activities: 'bg-orange-100 text-orange-800',
  culture: 'bg-purple-100 text-purple-800',
  news: 'bg-gray-100 text-gray-800',
  offers: 'bg-red-100 text-red-800',
};

const categoryLabels = {
  travel_tips: 'Travel Tips',
  destinations: 'Destinations',
  activities: 'Activities',
  culture: 'Culture',
  news: 'News',
  offers: 'Special Offers',
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [filters, setFilters] = useState({ category: '', status: '', search: '', page: 1, limit: 20 });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchPosts();
  }, [filters]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await getBlogPosts(filters);
      setPosts(data.data || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (postData) => {
    try {
      await createBlogPost(postData);
      toast.success('Blog post created successfully');
      setShowForm(false);
      fetchPosts();
    } catch (error) {
      toast.error('Failed to create blog post');
    }
  };

  const handleUpdate = async (id, postData) => {
    try {
      await updateBlogPost(id, postData);
      toast.success('Blog post updated successfully');
      setEditingPost(null);
      fetchPosts();
    } catch (error) {
      toast.error('Failed to update blog post');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await deleteBlogPost(id);
      toast.success('Blog post deleted successfully');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Blog Posts</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
        >
          <FiPlus className="mr-2" /> New Post
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
            >
              <option value="">All</option>
              {Object.entries(categoryLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 mb-1">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Title or content"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Blog Posts Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Author</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                 </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {posts.map((post) => (
                  <tr key={post._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-800">{post.title}</p>
                        <p className="text-xs text-gray-500 truncate max-w-md">{post.excerpt}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${categoryColors[post.category]}`}>
                        {categoryLabels[post.category]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">{post.author}</td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center">
                        <FiCalendar className="mr-1 text-gray-400 w-3 h-3" />
                        {post.published_at ? formatDate(post.published_at) : formatDate(post.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">{post.views || 0}</td>
                    <td className="px-6 py-4">
                      {post.status === 'published' ? (
                        <span className="flex items-center text-green-600 text-sm">
                          <FiEye className="mr-1" /> Published
                        </span>
                      ) : post.status === 'draft' ? (
                        <span className="flex items-center text-yellow-600 text-sm">
                          <FiEyeOff className="mr-1" /> Draft
                        </span>
                      ) : (
                        <span className="flex items-center text-gray-500 text-sm">
                          <FiEyeOff className="mr-1" /> Archived
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => setEditingPost(post)}
                        className="text-blue-500 hover:text-blue-700 mr-3"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(post._id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {posts.length === 0 && (
            <div className="text-center py-12 text-gray-500">No blog posts found</div>
          )}
          
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters({ ...filters, page })}
              />
            </div>
          )}
        </div>
      )}

      {/* Blog Post Form Modal */}
      <BlogPostForm
        isOpen={showForm || !!editingPost}
        onClose={() => {
          setShowForm(false);
          setEditingPost(null);
        }}
        onSubmit={editingPost ? (data) => handleUpdate(editingPost._id, data) : handleCreate}
        initialData={editingPost}
      />
    </div>
  );
}
